/**
 * itemMode — the ONE discriminant for "is this pane looking at a received email
 * or at a draft the user is writing?".
 *
 * Ported verbatim (logic and tests) from
 * `origin/claude/orachat-chat-modes:ui/apps/outlook-addin/src/features/composeSurface/itemMode.ts`
 * (30faf4e2a) for outlook-orachat-uplift s5 (design C54): every Outlook turn
 * carries its pane, `surface_mode: "compose"` in a compose window and nothing
 * in the reading pane, and this is where the pane comes from.
 *
 * Why this exists
 * ---------------
 * The pane mostly opens on a read item, and every Office read in `src/` is
 * written as if that were the only possibility. On a compose item
 * `useMailboxItem.ts` throws — `item.to` and `item.cc` are `Recipients`
 * OBJECTS, not arrays, so `?? []` never fires and `.map` is not a function —
 * which leaves `emailContext` null with no error shown. A compose pane must be
 * recognised as one before any of that code is trusted.
 *
 * Why feature detection and NOT `itemType`
 * ----------------------------------------
 * `Office.MailboxEnums.ItemType` distinguishes MESSAGE from APPOINTMENT, not
 * compose from read: `itemType === "message"` in BOTH modes. It is declared on
 * `MessageRead` (@types/office-js 1.0.592 index.d.ts:21403) and on
 * `MessageCompose` (:19437) alike, with no `[Api set: …]` marker on either.
 *
 * The real discriminants are the members that exist in only one mode:
 *   - `saveAsync`             — compose only (index.d.ts:20799/20840, [Api set: Mailbox 1.3])
 *   - `displayReplyFormAsync` — read only    (index.d.ts:21742/21771)
 *
 * We probe `saveAsync` because its requirement set (1.3) is BELOW the manifest's
 * base Mailbox 1.5 floor, so it is present on every host that can install this
 * add-in at all. `displayReplyFormAsync` is read as corroborating evidence only
 * (`readItemModeEvidence` reports both), never as the decision.
 *
 * Why the casts
 * -------------
 * `@types/office-js` models `Office.context.mailbox.item` as the INTERSECTION of
 * every mode (index.d.ts:17961: `Item & ItemCompose & ItemRead & Message &
 * MessageCompose & MessageRead & Appointment & …`). TypeScript therefore
 * believes `saveAsync` always exists and cannot help here at all. Every check
 * below is runtime-only and the structural casts are deliberate.
 */

import { useEffect, useState } from "react";

/**
 * - `read`    — a received message (or anything that is not a draft).
 * - `compose` — a draft/reply the user is writing.
 * - `none`    — no Office host, no mailbox, or no selected item.
 */
export type PaneItemMode = "read" | "compose" | "none";

/** The item members whose presence/absence decides the mode. */
export interface ItemModeEvidence {
  /** Whether an Office global with a mailbox item was reachable at all. */
  hasItem: boolean;
  /** `typeof item.saveAsync` — "function" on compose. */
  saveAsync: string;
  /** `typeof item.displayReplyFormAsync` — "function" on read. */
  displayReplyFormAsync: string;
  /** `typeof item.getItemIdAsync` — "function" on compose (Mailbox 1.8). */
  getItemIdAsync: string;
  /** `item.itemType` — "message" in BOTH modes; reported, never decisive. */
  itemType: string;
  /**
   * `item.displayMode` — NOT declared by @types/office-js for mail items. Probed
   * anyway and reported verbatim: what a host actually exposes in compose is
   * exactly what a diagnostic needs to see.
   */
  displayMode: string;
  /** Non-empty when reading the item itself threw. */
  error?: string;
}

/**
 * Structural view of the item. Deliberately NOT `Office.MessageRead` /
 * `Office.MessageCompose` — see the "Why the casts" note above.
 */
interface ItemLike {
  saveAsync?: unknown;
  displayReplyFormAsync?: unknown;
  getItemIdAsync?: unknown;
  itemType?: unknown;
  displayMode?: unknown;
}

/**
 * Read the Office global off `globalThis` rather than the ambient `Office`
 * binding: outside a host (jsdom, a plain browser tab) the identifier is not
 * declared at runtime and even `Office?.context` throws ReferenceError.
 */
function mailboxItem(): ItemLike | undefined {
  const office = (
    globalThis as { Office?: { context?: { mailbox?: { item?: ItemLike } } } }
  ).Office;
  return office?.context?.mailbox?.item ?? undefined;
}

/** Stringify a possibly-throwing property read. Never throws. */
function readTag(read: () => unknown): string {
  try {
    const v = read();
    if (v === undefined) return "<undefined>";
    if (v === null) return "<null>";
    return String(v);
  } catch {
    return "<threw>";
  }
}

/**
 * Everything the mode decision is based on, so a diagnostic can show the raw
 * evidence rather than just the verdict.
 */
export function readItemModeEvidence(): ItemModeEvidence {
  let item: ItemLike | undefined;
  try {
    item = mailboxItem();
  } catch (err) {
    return {
      hasItem: false,
      saveAsync: "<unreadable>",
      displayReplyFormAsync: "<unreadable>",
      getItemIdAsync: "<unreadable>",
      itemType: "<unreadable>",
      displayMode: "<unreadable>",
      error: String(err),
    };
  }
  if (!item) {
    return {
      hasItem: false,
      saveAsync: "<no item>",
      displayReplyFormAsync: "<no item>",
      getItemIdAsync: "<no item>",
      itemType: "<no item>",
      displayMode: "<no item>",
    };
  }
  return {
    hasItem: true,
    saveAsync: readTag(() => typeof item!.saveAsync),
    displayReplyFormAsync: readTag(() => typeof item!.displayReplyFormAsync),
    getItemIdAsync: readTag(() => typeof item!.getItemIdAsync),
    itemType: readTag(() => item!.itemType),
    displayMode: readTag(() => item!.displayMode),
  };
}

/** Derive the mode from already-read evidence (pure — the testable core). */
export function itemModeFromEvidence(evidence: ItemModeEvidence): PaneItemMode {
  if (!evidence.hasItem) return "none";
  return evidence.saveAsync === "function" ? "compose" : "read";
}

/**
 * The mode of the item this pane instance is attached to. Never throws — an
 * unreadable host degrades to `"none"`, which routes to the existing read
 * experience exactly as an empty mailbox does today.
 */
export function resolveItemMode(): PaneItemMode {
  return itemModeFromEvidence(readItemModeEvidence());
}

/**
 * `resolveItemMode()` as a hook.
 *
 * A pane instance's mode is fixed in practice — a compose window's pane never
 * becomes a read pane — but `ItemChanged` is subscribed anyway so a host that
 * reuses one pane across items cannot strand us in the wrong branch. Handler
 * registration mirrors useMailboxItem.ts: `Office.EventType.ItemChanged` is
 * looked up at runtime and the call is wrapped in try/catch — never a hard-coded
 * numeric event-type code.
 */
export function usePaneItemMode(): PaneItemMode {
  const [mode, setMode] = useState<PaneItemMode>(() => resolveItemMode());

  useEffect(() => {
    let cancelled = false;
    // Re-resolve once after mount: on some hosts `Office.onReady` fires before
    // the item is attached, so the lazy initialiser above can see nothing.
    setMode(resolveItemMode());

    let removeHandler: (() => void) | null = null;
    try {
      const office = (
        globalThis as {
          Office?: {
            EventType?: { ItemChanged?: unknown };
            context?: {
              mailbox?: {
                addHandlerAsync?: (t: unknown, h: () => void) => void;
                removeHandlerAsync?: (t: unknown, h: () => void) => void;
              };
            };
          };
        }
      ).Office;
      const evtType = office?.EventType?.ItemChanged;
      const mailbox = office?.context?.mailbox;
      if (evtType && mailbox?.addHandlerAsync && mailbox?.removeHandlerAsync) {
        const handler = () => {
          if (!cancelled) setMode(resolveItemMode());
        };
        mailbox.addHandlerAsync(evtType, handler);
        removeHandler = () => {
          try {
            mailbox.removeHandlerAsync!(evtType, handler);
          } catch {
            /* ignore */
          }
        };
      }
    } catch {
      // ItemChanged is best-effort; the resolve on mount still stands.
    }

    return () => {
      cancelled = true;
      if (removeHandler) removeHandler();
    };
  }, []);

  return mode;
}
